import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import {
  arrayRemove,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import { removeActivityFromFeedCache, useActivityInterested, useFeed } from '../hooks/useFeed';
import { useProfile } from '../hooks/useProfile';
import { usePlanJoinRequest, usePlanJoinRequests } from '../hooks/usePlanJoinRequest';
import { useUserProfile } from '../utils/userProfiles';
import { useInterestedUsers } from '../hooks/useInterestedUsers';
import LoadingSpinner from '../components/LoadingSpinner';
import TonyteBackground from '../components/TonyteBackground';
import AmbientGlow from '../components/AmbientGlow';
import AvatarStack from '../components/AvatarStack';
import VibeVerifiedBadge from '../components/VibeVerifiedBadge';
import TonyteModal from '../components/TonyteModal';
import { Colors, Typography, Radius, Shadow } from '../theme';
import type { RootStackParamList, TonyteActivity } from '../types';
import { getWebFallbackImage, resolveActivityImage } from '../utils/activityImages';
import { auth, FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import {
  deleteEventFromFirestore,
  ensureInterestedAndAddToCalendar,
  ensureInterestedAndJoinEventChat,
  isEventChatExpiredError,
  getActivityPrimaryLink,
  getActivityVenueLabel,
  getLocalizedActivityTime,
  openActivityInGoogleCalendar,
  openExternalUrl,
  openLocationInMaps,
  shareActivityExternally,
  getLumaRegistrationUrl,
  registerOnLumaThen,
} from '../utils/activityActions';
import { canManageEvent } from '../utils/eventPermissions';
import { requestNotificationPermission } from '../utils/permissions';
import { showTonyteToast } from '../utils/tonyteToast';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen, wasRecentlyTracked } from '../utils/eventAnalytics';
import { canAccessCity, normalizeCityKey } from '../utils/cities';
import EventShareSheet from '../components/EventShareSheet';
import { useI18n } from '../i18n';
import { requireVerifiedEmailForAction } from '../utils/verificationGate';
import { useTranslatedActivity } from '../hooks/useTranslatedActivity';
import { buildOrganizerSummaries, usePublisherVenue, useVenuePostInteractions } from '../hooks/useVenueVibes';
import { getSafeTags } from '../utils/activityTags';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { daylightStyle } from '../theme/daylightStyle';
import { getPlanMemberIds, isPlanMember } from '../utils/planMembership';
import { getStudiZoneTypeLabel } from '../utils/studiZoneCategories';

const { width } = Dimensions.get('window');
const HERO_HEIGHT = Math.min(430, Math.max(360, width * 1.02));
const PLAN_HERO_HEIGHT = Math.min(360, Math.max(300, width * 0.78));

const PLAN_HIGH_RISK = ['private', 'house', 'home', 'secret', 'drugs', 'weed', 'cash only', 'no police'];
const PLAN_MEDIUM_RISK = ['party', 'afterparty', 'drinks', 'alcohol', 'club', 'bar', 'night'];
type PlanRisk = 'low' | 'medium' | 'high';
function getPlanRisk(a: TonyteActivity): PlanRisk {
  const t = `${a.title} ${a.description ?? ''}`.toLowerCase();
  if (PLAN_HIGH_RISK.some(w => t.includes(w))) return 'high';
  if (PLAN_MEDIUM_RISK.some(w => t.includes(w))) return 'medium';
  return 'low';
}

type Props = NativeStackScreenProps<RootStackParamList, 'EventDetail' | 'StudiZoneEventDetail' | 'StudiZonePlanDetail' | 'StudiZonePlanDetailScreen'>;

function getTypeLabel(activity: TonyteActivity, t: (key: string) => string): string {
  if (activity.entryType === 'community_plan') return t('event.communityPlan');
  if (activity.sourceType === 'club') return t('event.clubEvent');
  if (activity.sourceType === 'generated_jam_session') return t('event.jamSession');
  return t('event.officialEvent');
}

function getVenueLabel(activity: TonyteActivity): string {
  return getActivityVenueLabel(activity) || activity.address || activity.location || activity.city || 'Tonyte venue';
}

const LOCALE_MAP: Record<string, string> = {
  de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU', en: 'en-US',
};

// ── Modal key type ──────────────────────────────────────────────────────────
type ModalKey = 'delete' | 'calendar' | 'share' | 'signIn' | 'interestedOrChat' | 'planChat' | 'cancelRequest' | 'adminLeave' | null;

const EventDetailScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t, tp, language } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createEventDetailAppearanceStyles(theme), [theme]);
  const locale = LOCALE_MAP[language] ?? 'en-US';
  const { eventId, event: passedEvent } = route.params;
  // [PushTapDebug] step 10 — proves navigation actually reached this screen
  // and mounted it, vs. navigate() being invoked but rendering failing
  // silently later. Temporary, remove after the debug pass.
  useEffect(() => {
    if (__DEV__) console.log('[PushTapDebug] EventDetail mounted', { routeName: route.name, eventId, hasPassedEvent: Boolean(passedEvent) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const insets = useSafeAreaInsets();
  const userId = auth.currentUser?.uid || null;
  const { profile } = useProfile(userId);
  const { activities, loading } = useFeed({ includeAdminOnlyCities: profile?.role === 'admin' });
  const isStudiZoneDetail = route.name === 'StudiZoneEventDetail' || route.name === 'StudiZonePlanDetail' || route.name === 'StudiZonePlanDetailScreen';
  const activity = useMemo(
    () => {
      if (isStudiZoneDetail && passedEvent) return passedEvent;
      const resolved = activities.find(item => getResolvedEventId(item) === eventId || item.id === eventId) || passedEvent || null;
      if (resolved && !canAccessCity(normalizeCityKey(resolved.cityKey || resolved.city), profile?.role)) return null;
      return resolved;
    },
    [activities, eventId, isStudiZoneDetail, passedEvent, profile?.role],
  );
  const translatedActivity = useTranslatedActivity(activity);
  const resolvedEventId = useMemo(() => getResolvedEventId(activity) || eventId, [activity, eventId]);
  const DetailScroller = Platform.OS === 'web' ? View : ScrollView;

  // Best-effort fallback for the universal/deep link path (tonyte://event/:id,
  // https://tonyte.app/event/:id), which React Navigation's linking config
  // resolves declaratively with no JS call site to instrument directly. Only
  // fires when nothing already tracked this open: `passedEvent` is present
  // for every in-app card/rail tap (all of which track themselves at the
  // call site before navigating), so its absence plus "not already tracked
  // in the last few seconds" (which also covers the notification-tap path,
  // whose own call site tracks a moment before this screen mounts) is what's
  // left over — a fresh deep link.
  useEffect(() => {
    if (!activity || passedEvent || wasRecentlyTracked(resolvedEventId)) return;
    trackEventDetailOpen({ eventId: resolvedEventId, organizerId: activity.hostId, source: ANALYTICS_SOURCES.SHARED_LINK });
  }, [activity, passedEvent, resolvedEventId]);
  const [imageSource, setImageSource] = useState<number | string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [usedImageFallback, setUsedImageFallback] = useState(false);
  const [chatPending, setChatPending] = useState(false);
  const [planJoinPending, setPlanJoinPending] = useState(false);
  const [planChatMember, setPlanChatMember] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const [highRiskAck, setHighRiskAck] = useState(false);
  const [joinRequestMsg, setJoinRequestMsg] = useState('');
  const [sendingJoinRequest, setSendingJoinRequest] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [freshParticipantIds, setFreshParticipantIds] = useState<string[] | null>(null);
  const [freshAdminIds, setFreshAdminIds] = useState<string[] | null>(null);
  const [shareSheetVisible, setShareSheetVisible] = useState(false);
  const [venuePostComment, setVenuePostComment] = useState('');
  const [venuePostComments, setVenuePostComments] = useState<any[]>([]);
  const [venuePostCommentsLoading, setVenuePostCommentsLoading] = useState(false);

  // ── Tonyte modal state ─────────────────────────────────────────────────────
  const [activeModal, setActiveModal] = useState<ModalKey>(null);
  const [signInMsg, setSignInMsg] = useState(t('auth.signInToUseFeature'));
  const closeModal = () => setActiveModal(null);

  const { interested, count, toggle, syncInterested } = useActivityInterested(resolvedEventId, userId, activity ?? undefined);
  const interestedUsers = useInterestedUsers(resolvedEventId);
  const isVenuePost = activity?.postedByType === 'venue';
  const venuePost = useVenuePostInteractions(isVenuePost ? activity : null, userId);
  const { loadComments: loadVenuePostComments } = venuePost;
  const publisherVenue = usePublisherVenue(activity, profile?.role === 'admin' || profile?.role === 'co-admin');
  const organizerProfile = useMemo(() => {
    if (!activity) return null;
    return buildOrganizerSummaries([activity])[0] || null;
  }, [activity]);

  const isCommunityPlanEarly = activity?.entryType === 'community_plan';
  const { request: joinRequest, sendRequest, cancelRequest } = usePlanJoinRequest(
    isCommunityPlanEarly ? (activity?.id ?? null) : null,
    userId,
  );
  const isOrganizerEarly = isCommunityPlanEarly && (activity?.createdBy === userId || activity?.hostId === userId);
  const { requests: pendingJoinRequests, acceptRequest, declineRequest } = usePlanJoinRequests(
    isOrganizerEarly ? (activity?.id ?? null) : null,
    isOrganizerEarly,
    userId,
  );

  useEffect(() => {
    if (!activity) return;
    let cancelled = false;
    setImageFailed(false);
    setUsedImageFallback(false);
    if (isStudiZoneDetail) {
      setImageSource(null);
      return undefined;
    }
    resolveActivityImage(activity)
      .then(resolved => {
        if (!cancelled) setImageSource(resolved.source);
      })
      .catch(error => {
        if (!cancelled) {
          setImageFailed(true);
          console.warn('[Tonyte][RN][EventDetailImage] failed', {
            eventId: activity.id,
            title: activity.title,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      });
    return () => { cancelled = true; };
  }, [activity, isStudiZoneDetail]);

  useEffect(() => {
    if (!activity || !userId || activity.entryType !== 'community_plan') return undefined;
    return onSnapshot(
      doc(firestore, 'planChats', activity.id),
      snap => {
        const ids = snap.exists() ? (snap.data().memberIds ?? []) : [];
        setPlanChatMember(Array.isArray(ids) && ids.includes(userId));
      },
      () => setPlanChatMember(false),
    );
  }, [activity, userId]);

  useEffect(() => {
    if (!activity || activity.entryType !== 'community_plan') return undefined;
    return onSnapshot(
      doc(firestore, FIRESTORE_COLLECTIONS.plans, activity.id),
      snap => {
        if (!snap.exists()) return;
        const data = snap.data();
        setFreshParticipantIds(Array.isArray(data.participantIds) ? data.participantIds : []);
        setFreshAdminIds(Array.isArray(data.adminIds) ? data.adminIds : []);
      },
      () => {},
    );
  }, [activity]);

  useEffect(() => {
    if (!activity || !isVenuePost) {
      setVenuePostComments([]);
      return;
    }
    setVenuePostCommentsLoading(true);
    loadVenuePostComments()
      .then(setVenuePostComments)
      .finally(() => setVenuePostCommentsLoading(false));
  }, [activity, isVenuePost, loadVenuePostComments]);

  const translatedTags = useMemo(() => {
    const all = getSafeTags(translatedActivity.tags);
    return { chips: all.slice(0, 6), overflow: Math.max(0, all.length - 6) };
  }, [translatedActivity.tags]);

  // Plan attendees as AvatarUser[]
  const planAvatarUsers = useMemo(
    () => (activity?.attendees ?? []).slice(0, 6).map(a => ({
      userId: a.id,
      displayName: a.name,
      photoURL: a.photo,
    })),
    [activity?.attendees],
  );

  if (loading || !activity) return <LoadingSpinner />;

  const venueLabel = getVenueLabel(activity);
  const localizedTime = getLocalizedActivityTime(activity, locale, t('event.dateTba'), t('event.timeTba'));
  const isCommunityPlan = activity.entryType === 'community_plan';
  const heroHeight = isCommunityPlan ? PLAN_HERO_HEIGHT : HERO_HEIGHT;
  const isOrganizerOfPlan = isCommunityPlan && (
    activity.createdBy === userId || activity.hostId === userId ||
    (freshAdminIds ?? activity.adminIds ?? []).includes(userId ?? '')
  );

  const livePlan = freshParticipantIds === null ? activity : { ...activity, participantIds: freshParticipantIds };
  const activePlanMemberIds = isCommunityPlan ? getPlanMemberIds(livePlan) : [];
  const isPlanParticipant = isCommunityPlan && isPlanMember(livePlan, userId);

  const isRemovedFromPlan = isCommunityPlan && userId
    ? (activity.removedUserIds ?? []).includes(userId)
    : false;

  const canSeeExactLocation = !isCommunityPlan || isOrganizerOfPlan || isPlanParticipant;
  const planRisk = isCommunityPlan ? getPlanRisk(activity) : 'low';
  const planRiskMap = {
    low:    { color: '#34D399', label: t('event.lowRisk'),  sub: t('event.lowRiskSub') },
    medium: { color: '#FBBF24', label: t('event.caution'),  sub: t('event.cautionSub') },
    high:   { color: '#F87171', label: t('event.highRisk'), sub: t('event.highRiskSub') },
  };
  const planRiskCfg = planRiskMap[planRisk];
  const memberCount = isCommunityPlan ? activePlanMemberIds.length : count;
  const isClubEvent = activity.sourceType === 'club';
  const supportsEventChat = activity.entryType === 'official_event' && !isClubEvent;
  const canManage = canManageEvent(profile ? { ...profile, uid: userId } : userId ? { uid: userId } : null, activity);
  const peopleGoingCount = isCommunityPlan ? memberCount : count;
  const subtitle = activity.shortNote || [venueLabel, localizedTime].filter(Boolean).join(' · ') || t('event.tonyteNight');

  const primaryLink = getActivityPrimaryLink(activity);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const shareEvent = () => setActiveModal('share');

  const openChatDetail = (memberIds?: string[], showPeople = false) => {
    const routeName = isStudiZoneDetail && isCommunityPlan ? 'StudiZonePlanChatScreen' : 'ChatDetail';
    navigation.navigate(routeName, {
      chatId: resolvedEventId,
      name: translatedActivity.title || activity.title,
      kind: isCommunityPlan ? 'plan' : 'event',
      photo: typeof imageSource === 'string' ? imageSource : undefined,
      participantIds: memberIds,
      showPeople,
    });
  };

  const handlePlanMembers = () => {
    if (!userId || (!isPlanParticipant && !isOrganizerOfPlan)) return;
    openChatDetail(activePlanMemberIds, true);
  };

  const handleChat = async () => {
    if (!userId) {
      setSignInMsg(t('event.signInToJoinChats'));
      setActiveModal('signIn');
      return;
    }
    if (!supportsEventChat) {
      showTonyteToast(t('event.chatUnavailable'), 'info', t('event.noEventChat'));
      return;
    }
    if (!requireVerifiedEmailForAction()) return;
    setChatPending(true);
    try {
      await ensureInterestedAndJoinEventChat(activity, userId, interested, t);
      syncInterested(true);
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      openChatDetail();
    } catch (error) {
      console.warn('[Tonyte][RN][EventChat] join action failed', {
        path: `${FIRESTORE_COLLECTIONS.eventChats}/${resolvedEventId}`,
        action: 'ensureInterestedAndJoinEventChat',
        activityId: activity.id,
        resolvedEventId,
        userId,
        error,
      });
      if (isEventChatExpiredError(error)) {
        showTonyteToast(t('event.chatNoLongerAvailable'), 'info', t('event.chatNoLongerAvailableBody'));
      } else {
        showTonyteToast(t('event.couldNotJoinChat'), 'error', t('common.tryAgain'));
      }
    } finally {
      setChatPending(false);
    }
  };

  const handleInterestedPress = () => {
    if (!userId) {
      setSignInMsg(t('event.signInToMarkInterested'));
      setActiveModal('signIn');
      return;
    }
    if (interested || !supportsEventChat) {
      void toggle();
      return;
    }
    if (!requireVerifiedEmailForAction()) return;
    setActiveModal('interestedOrChat');
  };

  const handlePlanJoinPress = () => {
    if (!userId) {
      setSignInMsg(t('event.signInToJoinPlans'));
      setActiveModal('signIn');
      return;
    }
    if (isRemovedFromPlan) {
      showTonyteToast(t('event.removedFromPlan'), 'error');
      return;
    }
    if (!requireVerifiedEmailForAction()) return;
    setHighRiskAck(false);
    setJoinRequestMsg('');
    setShowSafety(true);
  };

  const confirmPlanJoinRequest = async () => {
    if (!activity || !userId) return;
    setShowSafety(false);
    setSendingJoinRequest(true);
    try {
      await sendRequest(activity, joinRequestMsg);
      setJoinRequestMsg('');
      showTonyteToast(t('event.requestSent'), 'success', t('event.organizerWillReview'));
    } catch {
      showTonyteToast(t('event.couldNotSendRequest'), 'error', t('common.tryAgain'));
    } finally {
      setSendingJoinRequest(false);
    }
  };

  const handlePlanLeave = async () => {
    if (!activity || !userId) return;
    if (isOrganizerOfPlan) {
      setActiveModal('adminLeave');
      return;
    }
    setPlanJoinPending(true);
    try {
      const existing = activity.attendees?.find(a => a.id === userId || (a as any).userId === userId);
      const planRef = doc(firestore, FIRESTORE_COLLECTIONS.plans, activity.id);
      const snap = await getDoc(planRef);
      const cur = typeof snap.data()?.participants === 'number' ? (snap.data()!.participants as number) : 0;
      await updateDoc(planRef, {
        participantIds: arrayRemove(userId),
        members: arrayRemove(userId),
        ...(existing ? { attendees: arrayRemove(existing) } : {}),
        participants: Math.max(cur - 1, 0),
      });
      await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.planChats, activity.id), {
        memberIds: arrayRemove(userId),
      });
      deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, activity.id, 'participants', userId)).catch(() => {});
      showTonyteToast(t('event.leftPlan'), 'info');
    } catch {
      showTonyteToast(t('event.couldNotLeavePlan'), 'error', t('common.tryAgain'));
    } finally {
      setPlanJoinPending(false);
    }
  };

  const doDeletePlan = async () => {
    if (!activity || !userId || deletingPlan) return;
    setDeletingPlan(true);
    closeModal();
    try {
      await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, activity.id));
      try { await deleteDoc(doc(firestore, 'planChats', activity.id)); } catch {}
      removeActivityFromFeedCache(activity.id);
      navigation.goBack();
      showTonyteToast(t('event.planDeleted'), 'success');
    } catch {
      setDeletingPlan(false);
      showTonyteToast(t('event.couldNotDeletePlan'), 'error', t('common.tryAgain'));
    }
  };

  const handlePlanChat = () => {
    if (!userId) {
      setSignInMsg(t('auth.signInToUseFeature'));
      setActiveModal('signIn');
      return;
    }
    if (!isPlanParticipant && !isOrganizerOfPlan) {
      setActiveModal('planChat');
      return;
    }
    openChatDetail();
  };

  const confirmDelete = () => setActiveModal(isCommunityPlanEarly ? 'adminLeave' : 'delete');

  const doDelete = () => {
    closeModal();
    removeActivityFromFeedCache(activity.id);
    navigation.goBack();
    void deleteEventFromFirestore(activity, userId || '').catch(() => {
      showTonyteToast(t('event.couldNotDeleteEvent'), 'error', t('common.tryAgain'));
    });
  };

  const submitVenuePostComment = async () => {
    await venuePost.addComment(venuePostComment);
    setVenuePostComment('');
    setVenuePostComments(await loadVenuePostComments());
  };

  const deleteVenuePostComment = async (commentId: string, commentUserId?: string) => {
    await venuePost.deleteComment(commentId, commentUserId);
    setVenuePostComments(await loadVenuePostComments());
  };

  // ── Calendar ───────────────────────────────────────────────────────────────

  const handleAddToCalendar = () => {
    if (!activity.startDate && !activity.startTime) {
      showTonyteToast(t('event.noDateAvailable'), 'error', t('event.noScheduledTime'));
      return;
    }
    setActiveModal('calendar');
  };

  const doAddToCalendar = async () => {
    closeModal();
    // Signed-in users: adding to calendar also marks the event interested
    // (same as Join Chat). Signed-out visitors can still open the calendar.
    if (!userId) {
      await openActivityInGoogleCalendar(activity, t);
      return;
    }
    if (await ensureInterestedAndAddToCalendar(activity, userId, t)) syncInterested(true);
  };

  // From the "Join event chat?" prompt: like Join Chat, adding to calendar
  // also marks the event interested.
  const handleInterestedAddToCalendar = async () => {
    if (!userId) {
      setSignInMsg(t('auth.signInToUseFeature'));
      setActiveModal('signIn');
      return;
    }
    if (await ensureInterestedAndAddToCalendar(activity, userId, t)) syncInterested(true);
  };

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <DetailScroller
        style={Platform.OS === 'web' ? styles.webDetailScroller : styles.scroll}
        {...(Platform.OS === 'web' ? {} : {
          scrollEnabled: true,
          nestedScrollEnabled: true,
          keyboardShouldPersistTaps: 'handled' as const,
          keyboardDismissMode: 'on-drag' as const,
          showsVerticalScrollIndicator: false,
          contentContainerStyle: { paddingBottom: insets.bottom + 178 },
        })}
      >
        <View style={Platform.OS === 'web' ? { paddingBottom: insets.bottom + 178 } : undefined}>
        {/* ── Hero ── */}
        <View style={[styles.hero, isStudiZoneDetail && !isCommunityPlan && styles.studiZoneHero, { height: isCommunityPlan ? insets.top + 80 : heroHeight, paddingTop: insets.top + 72 }]}>
          {isStudiZoneDetail && !isCommunityPlan ? (
            <>
              <LinearGradient
                colors={['#073B43', '#0B5E63', '#123B52', '#07131F']}
                locations={[0, 0.38, 0.72, 1]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
              />
              <View pointerEvents="none" style={styles.studiZoneHeroMark}>
                <Icon name="book-open" size={154} color="rgba(125,211,252,0.10)" />
              </View>
              <View pointerEvents="none" style={styles.studiZoneDotField}>
                {Array.from({ length: 12 }, (_, index) => <View key={`student-dot-${index}`} style={styles.studiZoneDot} />)}
              </View>
            </>
          ) : null}
          {!isCommunityPlan && !isStudiZoneDetail && imageSource && !imageFailed ? (
            <TouchableOpacity style={styles.heroImage} activeOpacity={0.96} onPress={() => setImageViewerVisible(true)} accessibilityRole="button" accessibilityLabel={t('imageCrop.viewFullImage')}>
              <Image source={typeof imageSource === 'number' ? imageSource : { uri: imageSource }} style={styles.heroImageBackdrop} resizeMode="cover" resizeMethod="resize" blurRadius={18} />
              <View style={styles.heroImageShade} />
              <Image
              source={typeof imageSource === 'number' ? imageSource : { uri: imageSource }}
              style={styles.heroImageContained}
              resizeMode="contain" resizeMethod="resize"
              onError={(event) => {
                // Same fallback as EventCard.tsx: the resolved URL can 404
                // or (confirmed live, tonyte.app's hosted /event-images/
                // paths) return HTTP 200 with an HTML page instead of the
                // real file — a missing-upload issue on that server, not
                // something this app controls. Without a fallback here the
                // hero was left as a bare gradient over blank space; swap
                // to the same themed Unsplash fallback once before giving up.
                if (!usedImageFallback) {
                  setUsedImageFallback(true);
                  setImageSource(getWebFallbackImage(activity));
                } else {
                  setImageFailed(true);
                }
                console.warn('[Tonyte][RN][EventDetailImage] load error', {
                  eventId: activity.id,
                  source: imageSource,
                  nativeEvent: event.nativeEvent,
                });
              }}
              />
            </TouchableOpacity>
          ) : null}
          <LinearGradient
            colors={['rgba(3,7,15,0.52)', 'rgba(3,7,15,0.12)', 'rgba(3,7,15,0.58)', 'rgba(5,8,18,0.98)']}
            locations={[0, 0.34, 0.68, 1]}
            style={StyleSheet.absoluteFill}
          />
          <LinearGradient
            colors={['rgba(3,7,15,0.52)', 'rgba(3,7,15,0)', 'rgba(3,7,15,0.5)']}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={StyleSheet.absoluteFill}
          />
          {!isCommunityPlan && (
            <>
              <AmbientGlow tone="teal" size={250} intensity={0.38} style={styles.heroTealBloom} />
              <AmbientGlow tone="orange" size={290} intensity={0.42} style={styles.heroSunsetBloom} />
            </>
          )}

          <TouchableOpacity
            style={[styles.navButton, styles.backButton, { top: insets.top + 16 }]}
            onPress={() => navigation.goBack()}
            activeOpacity={0.82}
          >
            <Icon name="arrow-left" size={21} color="#fff" />
          </TouchableOpacity>

          <View style={[styles.heroActions, { top: insets.top + 16 }]}>
            <TouchableOpacity style={styles.navButton} activeOpacity={0.82} onPress={shareEvent}>
              <Icon name="share-2" size={18} color="#fff" />
            </TouchableOpacity>
            {!isCommunityPlan ? (
              <TouchableOpacity style={styles.navButton} activeOpacity={0.82} onPress={handleInterestedPress}>
                <Icon name="heart" size={18} color={interested ? Colors.turquoise : '#fff'} />
              </TouchableOpacity>
            ) : null}
          </View>

          {!isCommunityPlan && (
            <View style={[styles.inviteCard, appearance.inviteCard, styles.inviteEventPosition, isStudiZoneDetail && styles.studiZoneInviteCard]}>
              <View style={styles.chipRow}>
                <View style={[styles.detailChip, isStudiZoneDetail ? styles.chipStudent : styles.chipSunset]}>
                  {isStudiZoneDetail ? <Icon name="check-circle" size={11} color="#A7F3D0" /> : null}
                  <Text style={[styles.detailChipText, isStudiZoneDetail ? styles.studentChipText : appearance.chipText]}>
                    {isStudiZoneDetail ? 'StudiZone · Verified students' : getTypeLabel(activity, t)}
                  </Text>
                </View>
                {isStudiZoneDetail ? (
                  <View style={[styles.detailChip, styles.chipSoft]}>
                    <Icon name="book-open" size={11} color="#BAE6FD" />
                    <Text style={[styles.detailChipText, styles.studentCampusText]}>{getStudiZoneTypeLabel(activity)}</Text>
                  </View>
                ) : null}
                {activity.areaNote ? (
                  <View style={[styles.detailChip, styles.chipSoft, appearance.controlSurface]}>
                    <Text style={[styles.detailChipText, appearance.chipText]}>{activity.areaNote}</Text>
                  </View>
                ) : null}
              </View>
              <Text style={[styles.heroTitle, appearance.textPrimary, isStudiZoneDetail && styles.studiZoneHeroTitle]}>{translatedActivity.translatingTitle ? '...' : translatedActivity.title}</Text>
              <Text style={[styles.heroSubtitle, appearance.textSecondary, isStudiZoneDetail && styles.studiZoneHeroSubtitle]} numberOfLines={1}>{subtitle}</Text>
              <View style={styles.heroMetaGrid}>
                <MetaPill icon="map-pin" accent={isStudiZoneDetail ? '#7DD3FC' : Colors.sunset} text={venueLabel} />
                <MetaPill icon="calendar" accent={Colors.turquoise} text={localizedTime} />
              </View>
              <View style={[styles.peopleRow, appearance.controlSurface, isStudiZoneDetail && styles.studiZonePeopleRow]}>
                <View style={styles.peopleLeft}>
                  <Icon name="heart" size={14} color={interested ? Colors.turquoise : (theme.isDark ? 'rgba(255,255,255,0.62)' : theme.colors.icon)} />
                  <Text style={[styles.peopleText, appearance.textSecondary, isStudiZoneDetail && styles.studiZoneHeroSubtitle]}>{tp('event.interestedCount', peopleGoingCount)}</Text>
                </View>
                <AvatarStack users={interestedUsers} totalCount={count} size={26} />
              </View>
              {canManage ? (
                <View style={styles.detailManageActions}>
                  <TouchableOpacity activeOpacity={0.82} style={[styles.detailManageButton, appearance.controlSurface]} onPress={() => navigation.navigate('UploadEvent', { event: activity })}>
                    <Text style={[styles.detailManageButtonText, appearance.textPrimary]}>{t('common.edit')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity activeOpacity={0.82} style={[styles.detailManageButton, appearance.controlSurface]} onPress={confirmDelete}>
                    <Text style={[styles.detailManageButtonText, styles.detailManageDeleteText]}>{t('common.delete')}</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          )}
        </View>

        <View style={[styles.content, appearance.content, isCommunityPlan ? styles.contentPlan : styles.contentEvent]}>

          {isCommunityPlan && (
            <View style={[styles.planInviteCard, appearance.inviteCard]}>
              <View style={styles.chipRow}>
                <View style={[styles.detailChip, styles.chipTeal]}>
                  <Text style={[styles.detailChipText, appearance.chipText]}>{getTypeLabel(activity, t)}</Text>
                </View>
                {activity.areaNote ? (
                  <View style={[styles.detailChip, styles.chipSoft, appearance.controlSurface]}>
                    <Text style={[styles.detailChipText, appearance.chipText]}>{activity.areaNote}</Text>
                  </View>
                ) : null}
              </View>
              <Text style={[styles.heroTitle, appearance.textPrimary]}>{translatedActivity.translatingTitle ? '...' : translatedActivity.title}</Text>
              <Text style={[styles.heroSubtitle, appearance.textSecondary]} numberOfLines={1}>{subtitle}</Text>
              <View style={styles.heroMetaGrid}>
                <MetaPill icon="map-pin" accent={Colors.sunset} text={venueLabel} />
                <MetaPill icon="calendar" accent={Colors.turquoise} text={localizedTime} />
              </View>
              <TouchableOpacity
                style={[styles.peopleRow, appearance.controlSurface]}
                onPress={handlePlanMembers}
                disabled={!isPlanParticipant && !isOrganizerOfPlan}
                activeOpacity={0.8}
                accessibilityRole="button"
                accessibilityLabel={tp('event.memberCount', memberCount)}
              >
                <View style={styles.peopleLeft}>
                  <Icon name="users" size={14} color={Colors.turquoise} />
                  <Text style={[styles.peopleText, appearance.textSecondary]}>{tp('event.memberCount', memberCount)}</Text>
                </View>
                <AvatarStack users={planAvatarUsers} totalCount={memberCount} size={26} />
              </TouchableOpacity>
              {canManage ? (
                <View style={styles.detailManageActions}>
                  <TouchableOpacity activeOpacity={0.82} style={[styles.detailManageButton, appearance.controlSurface]} onPress={confirmDelete}>
                    <Text style={[styles.detailManageButtonText, styles.detailManageDeleteText]}>{t('common.delete')}</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          )}

          <GlassCard>
            <Text style={[styles.cardTitle, appearance.textPrimary]}>{isCommunityPlan ? t('event.aboutThisPlan') : t('event.aboutThisEvent')}</Text>
            {publisherVenue ? (
              <TouchableOpacity
                activeOpacity={0.84}
                style={[styles.organizerProfileButton, styles.publisherButton, appearance.controlSurface]}
                onPress={() => navigation.navigate('VenueProfile', { venueId: publisherVenue.id })}
              >
                <View style={styles.organizerProfileAvatar}>
                  {publisherVenue.logoUrl ? (
                    <Image source={{ uri: publisherVenue.logoUrl }} style={styles.organizerProfileAvatarImage} />
                  ) : (
                    <Icon name="map-pin" size={14} color="#fff" />
                  )}
                </View>
                <View style={styles.organizerProfileCopy}>
                  <Text style={[styles.organizerProfileEyebrow, appearance.textMuted]}>{t('event.publishedBy')}</Text>
                  <Text style={[styles.organizerProfileName, appearance.textPrimary]} numberOfLines={1}>{publisherVenue.name}</Text>
                </View>
                <Icon name="chevron-right" size={18} color={theme.isDark ? 'rgba(255,255,255,0.56)' : theme.colors.iconMuted} />
              </TouchableOpacity>
            ) : null}
            <Text style={[styles.description, appearance.textSecondary]}>
              {translatedActivity.translatingDescription ? t('common.loading') : (translatedActivity.description || t('event.detailsFinalized'))}
            </Text>
            {activity.shortNote ? <Text style={[styles.shortNote, appearance.textSecondary]}>{activity.shortNote}</Text> : null}
            {isCommunityPlan && (
              <View style={[styles.planRiskBar, appearance.controlSurface]}>
                <View style={[styles.planRiskStripe, { backgroundColor: planRiskCfg.color }]} />
                <View style={styles.planRiskText}>
                  <Text style={[styles.planRiskLabel, { color: planRiskCfg.color }]}>{planRiskCfg.label}</Text>
                  <Text style={[styles.planRiskSub, appearance.textMuted]}>{planRiskCfg.sub}</Text>
                </View>
              </View>
            )}
            {activity.lineup?.length || activity.musicGenres?.length ? (
              <View style={styles.miniGrid}>
                {activity.lineup?.length ? <MiniCard label={t('event.lineup')} value={activity.lineup.join(' · ')} /> : null}
                {activity.musicGenres?.length ? <MiniCard label={t('event.genres')} value={activity.musicGenres.join(' · ')} /> : null}
              </View>
            ) : null}
            {translatedTags.chips.length ? (
              <View style={styles.tagRow}>
                {translatedTags.chips.map((tag, index) => <Tag key={`${activity.id}-${tag}-${index}`} label={tag} />)}
                {translatedTags.overflow ? <Tag key={`${activity.id}-overflow-tags`} label={`+${translatedTags.overflow}`} /> : null}
              </View>
            ) : null}
            <View style={styles.separator} />
            <View style={styles.linkRow}>
              {canSeeExactLocation ? (
                <TouchableOpacity onPress={() => void openLocationInMaps(activity, { provider: 'google', fallbackTitle: t('event.couldNotOpenMaps'), translate: t })}>
                  <Text style={styles.textLink}>{t('event.openGoogleMaps')}</Text>
                </TouchableOpacity>
              ) : null}
              {primaryLink ? (
                <TouchableOpacity onPress={() => void openExternalUrl(primaryLink, t('event.couldNotOpenEventLink'), t)}>
                  <Text style={[styles.textLinkMuted, appearance.textSecondary]}>{t('event.viewOfficialEvent')}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {activity.lumaRegistrationUrl ? (
              <SecondaryButton
                icon="external-link"
                label="Register on Luma"
                onPress={() => void openExternalUrl(activity.lumaRegistrationUrl!, 'Could not open Luma registration', t)}
              />
            ) : null}
            {activity.maxParticipants > 0 ? (
              <Text style={[styles.capacityText, appearance.textMuted]}>{t('event.capacity')}: {peopleGoingCount}/{activity.maxParticipants}</Text>
            ) : null}
          </GlassCard>

          {isVenuePost ? (
            <GlassCard>
              <View style={styles.cardHeaderRow}>
                <Text style={[styles.cardTitle, appearance.textPrimary]}>{t('event.venuePost')}</Text>
                <View style={[styles.detailChip, styles.chipSoft, appearance.controlSurface]}>
                  <Text style={[styles.detailChipText, appearance.chipText]}>{activity.venueName || activity.locationName || 'Tonyte'}</Text>
                </View>
              </View>
              {organizerProfile ? (
                <TouchableOpacity
                  activeOpacity={0.84}
                  style={[styles.organizerProfileButton, appearance.controlSurface]}
                  onPress={() => navigation.navigate('OrganizerProfile', { organizerId: organizerProfile.id, organizer: organizerProfile })}
                >
                  <View style={styles.organizerProfileAvatar}>
                    {organizerProfile.avatarUrl ? (
                      <Image source={{ uri: organizerProfile.avatarUrl }} style={styles.organizerProfileAvatarImage} />
                    ) : (
                      <Icon name="user" size={14} color="#fff" />
                    )}
                  </View>
                  <View style={styles.organizerProfileCopy}>
                    <Text style={[styles.organizerProfileEyebrow, appearance.textMuted]}>{t('event.hostedBy')}</Text>
                    <Text style={[styles.organizerProfileName, appearance.textPrimary]} numberOfLines={1}>{organizerProfile.displayName}</Text>
                  </View>
                  <Icon name="chevron-right" size={18} color="rgba(255,255,255,0.56)" />
                </TouchableOpacity>
              ) : null}
              <View style={styles.venuePostActions}>
                <TouchableOpacity disabled={venuePost.pending} onPress={venuePost.toggleLike} style={[styles.venuePostActionButton, appearance.controlSurface]} activeOpacity={0.84}>
                  <Icon name="heart" size={18} color={venuePost.liked ? '#D95283' : (theme.isDark ? '#fff' : theme.colors.icon)} />
                  <Text style={[styles.venuePostActionText, appearance.textPrimary]}>{venuePost.likesCount}</Text>
                </TouchableOpacity>
                <View style={[styles.venuePostActionButton, appearance.controlSurface]}>
                  <Icon name="message-circle" size={18} color={theme.isDark ? '#fff' : theme.colors.icon} />
                  <Text style={[styles.venuePostActionText, appearance.textPrimary]}>{venuePost.commentsCount}</Text>
                </View>
                <TouchableOpacity onPress={venuePost.sharePost} style={[styles.venuePostActionButton, appearance.controlSurface]} activeOpacity={0.84}>
                  <Icon name="send" size={18} color={theme.isDark ? '#fff' : theme.colors.icon} />
                  <Text style={[styles.venuePostActionText, appearance.textPrimary]}>{venuePost.sharesCount}</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.venueCommentInputWrap}>
                <TextInput
                  value={venuePostComment}
                  onChangeText={setVenuePostComment}
                  placeholder={t('event.addComment')}
                  placeholderTextColor={theme.isDark ? 'rgba(255,255,255,0.42)' : theme.colors.textMuted}
                  style={[styles.venueCommentInput, appearance.controlSurface, appearance.textPrimary]}
                  multiline
                />
                <TouchableOpacity onPress={submitVenuePostComment} style={styles.venueCommentSend} activeOpacity={0.84}>
                  <Icon name="arrow-up" size={16} color="#fff" />
                </TouchableOpacity>
              </View>
              <Text style={[styles.venueCommentsTitle, appearance.textPrimary]}>{t('event.comments')}</Text>
              {venuePostCommentsLoading ? <ActivityIndicator color={theme.isDark ? '#fff' : theme.colors.accent} /> : null}
              {venuePostComments.map(item => (
                <View key={item.id} style={styles.venueCommentRow}>
                  <View style={styles.venueCommentAvatar}>
                    {item.userPhotoUrl ? <Image source={{ uri: item.userPhotoUrl }} style={styles.venueCommentAvatarImage} /> : <Icon name="user" size={13} color="#fff" />}
                  </View>
                  <View style={styles.venueCommentBody}>
                    <Text style={[styles.venueCommentName, appearance.textPrimary]}>{item.userName || t('event.tonyteUser')}</Text>
                    <Text style={[styles.venueCommentText, appearance.textSecondary]}>{item.text}</Text>
                  </View>
                  {item.userId === userId ? (
                    <TouchableOpacity onPress={() => deleteVenuePostComment(item.id, item.userId)} style={styles.venueCommentDelete} activeOpacity={0.84}>
                      <Icon name="trash-2" size={14} color="rgba(255,255,255,0.72)" />
                    </TouchableOpacity>
                  ) : null}
                </View>
              ))}
            </GlassCard>
          ) : null}

          <GlassCard>
            <View style={styles.cardHeaderRow}>
              <Text style={[styles.cardTitle, appearance.textPrimary]}>{t('event.dateTime')}</Text>
              <View style={[styles.detailChip, styles.chipSoft, appearance.controlSurface]}>
                <Text style={[styles.detailChipText, appearance.chipText]}>{t('event.calendar')}</Text>
              </View>
            </View>
            <MetaPill icon="calendar" accent={Colors.turquoise} text={localizedTime} />
            <SecondaryButton icon="calendar" label={t('event.addToCalendar')} onPress={handleAddToCalendar} />
          </GlassCard>

          <GlassCard>
            <View style={styles.cardHeaderRow}>
              <Text style={[styles.cardTitle, appearance.textPrimary]}>{t('event.location')}</Text>
              <View style={[styles.detailChip, styles.chipSoft, appearance.controlSurface]}>
                <Text style={[styles.detailChipText, appearance.chipText]}>{activity.city || 'Map'}</Text>
              </View>
            </View>
            {canSeeExactLocation ? (
              <>
                {/* The whole map box opens the same maps link as "Open in Maps" —
                    people tap the big preview far more than the button. */}
                <TouchableOpacity
                  style={[styles.mapPreview, appearance.mapPreview]}
                  onPress={() => void openLocationInMaps(activity, { provider: 'apple', fallbackTitle: t('event.couldNotOpenMaps'), translate: t })}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel={`${t('event.openMaps')}: ${venueLabel}`}
                  testID="event-map-preview"
                >
                  <View style={styles.mapGrid} />
                  <View style={[styles.mapRoute, styles.mapRouteOne]} />
                  <View style={[styles.mapRoute, styles.mapRouteTwo]} />
                  <View style={styles.mapPin}>
                    <Icon name="map-pin" size={20} color="#fff" />
                  </View>
                  <View style={styles.mapCopy}>
                    <Text style={[styles.mapVenue, appearance.textPrimary]} numberOfLines={1}>{venueLabel}</Text>
                    <Text style={[styles.mapAddress, appearance.textSecondary]} numberOfLines={1}>{activity.address || activity.city}</Text>
                  </View>
                </TouchableOpacity>
                <SecondaryButton icon="map-pin" label={t('event.openMaps')} onPress={() => void openLocationInMaps(activity, { provider: 'apple', fallbackTitle: t('event.couldNotOpenMaps'), translate: t })} />
              </>
            ) : (
              <View style={[styles.locationLockedWrap, appearance.controlSurface]}>
                <Icon name="lock" size={20} color="rgba(255,255,255,0.35)" />
                <Text style={[styles.locationLockedText, appearance.textSecondary]}>
                  {t('event.exactLocationAfterApproval')}
                </Text>
                <Text style={[styles.locationCityHint, appearance.textMuted]}>{activity.city || ''}</Text>
              </View>
            )}
          </GlassCard>
          {isCommunityPlan && isOrganizerOfPlan && pendingJoinRequests.length > 0 && (
            <GlassCard>
              <Text style={[styles.cardTitle, appearance.textPrimary]}>
                {t('event.joinRequests')}
                <Text style={styles.requestBadge}> {pendingJoinRequests.length}</Text>
              </Text>
              {pendingJoinRequests.map(req => (
                <RequestReviewCard
                  key={req.id}
                  request={req}
                  onAccept={() => void acceptRequest(req, activity, userId ?? '').catch(() =>
                    showTonyteToast(t('event.couldNotAccept'), 'error', t('common.tryAgain')))}
                  onDecline={() => void declineRequest(req.id, userId ?? '').catch(() =>
                    showTonyteToast(t('event.couldNotDecline'), 'error', t('common.tryAgain')))}
                />
              ))}
            </GlassCard>
          )}
        </View>
        </View>
      </DetailScroller>

      {/* ── Plan join request modal ── */}
      {isCommunityPlan && (
        <Modal
          transparent
          animationType="fade"
          visible={showSafety}
          statusBarTranslucent
          onRequestClose={sendingJoinRequest ? undefined : () => setShowSafety(false)}
        >
          <Pressable style={styles.safetyOverlay} onPress={sendingJoinRequest ? undefined : () => setShowSafety(false)}>
            <Pressable style={styles.safetyCard} onPress={() => {}}>
              <Text style={styles.safetyTitle}>{t('event.requestToJoin')}</Text>
              <Text style={styles.safetyPlanName} numberOfLines={2}>{translatedActivity.title || activity.title}</Text>
              <View style={[styles.planRiskBar, appearance.controlSurface]}>
                <View style={[styles.planRiskStripe, { backgroundColor: planRiskCfg.color }]} />
                <View style={styles.planRiskText}>
                  <Text style={[styles.planRiskLabel, { color: planRiskCfg.color }]}>{planRiskCfg.label}</Text>
                  <Text style={[styles.planRiskSub, appearance.textMuted]}>{planRiskCfg.sub}</Text>
                </View>
              </View>
              {planRisk !== 'low' && (
                <View style={[styles.safetyWarning, { borderColor: `${planRiskCfg.color}44` }]}>
                  <Icon name={planRisk === 'high' ? 'alert-triangle' : 'alert-circle'} size={15} color={planRiskCfg.color} />
                  <Text style={[styles.safetyWarnText, { color: planRiskCfg.color }]}>
                    {planRisk === 'high'
                      ? t('event.highRiskWarning')
                      : t('event.mediumRiskWarning')}
                  </Text>
                </View>
              )}
              {planRisk === 'high' && (
                <TouchableOpacity style={styles.ackRow} onPress={() => setHighRiskAck(v => !v)} activeOpacity={0.8}>
                  <View style={[styles.ackBox, highRiskAck && styles.ackBoxChecked]}>
                    {highRiskAck && <Icon name="check" size={12} color="#fff" />}
                  </View>
                  <Text style={styles.ackText}>{t('event.ackRisks')}</Text>
                </TouchableOpacity>
              )}
              <TextInput
                style={styles.joinRequestMsgInput}
                placeholder={t('event.messageOrganizerOptional')}
                placeholderTextColor="rgba(255,255,255,0.30)"
                value={joinRequestMsg}
                onChangeText={setJoinRequestMsg}
                multiline
                numberOfLines={2}
                maxLength={200}
                editable={!sendingJoinRequest}
              />
              <View style={styles.safetyBtns}>
                <TouchableOpacity
                  style={styles.safetyCancel}
                  onPress={sendingJoinRequest ? undefined : () => { setShowSafety(false); setJoinRequestMsg(''); }}
                  activeOpacity={sendingJoinRequest ? 1 : 0.8}
                >
                  <Text style={styles.safetyCancelText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.safetyConfirm,
                    (planRisk === 'high' && !highRiskAck || sendingJoinRequest) && { backgroundColor: 'rgba(242,125,38,0.35)' },
                  ]}
                  onPress={(planRisk === 'high' && !highRiskAck) || sendingJoinRequest ? undefined : () => void confirmPlanJoinRequest()}
                  activeOpacity={(planRisk === 'high' && !highRiskAck) || sendingJoinRequest ? 1 : 0.8}
                >
                  {sendingJoinRequest
                    ? <ActivityIndicator size="small" color="#fff" />
                    : <Text style={styles.safetyConfirmText}>{t('event.sendRequest')}</Text>
                  }
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </Modal>
      )}

      {/* ── Tonyte themed modals ── */}

      {/* Delete confirmation */}
      <TonyteModal
        visible={activeModal === 'delete'}
        title={t('event.deleteEventQuestion')}
        message={t('event.deleteEventConfirmShort')}
        icon="trash-2"
        iconColor={Colors.error}
        buttons={[
          { label: t('common.cancel'), variant: 'secondary', onPress: closeModal },
          { label: t('common.delete'), variant: 'danger', onPress: doDelete },
        ]}
        onDismiss={closeModal}
      />

      {/* Calendar confirmation */}
      <TonyteModal
        visible={activeModal === 'calendar'}
        title={t('event.addToCalendar')}
        message={`${translatedActivity.title || activity.title}\n${localizedTime}${venueLabel ? `\n${venueLabel}` : ''}`}
        icon="calendar"
        iconColor={Colors.turquoise}
        buttons={[
          { label: t('common.cancel'), variant: 'secondary', onPress: closeModal },
          { label: t('event.openGoogleCalendar'), variant: 'teal', onPress: () => void doAddToCalendar() },
        ]}
        onDismiss={closeModal}
      />

      {/* Share options */}
      <TonyteModal
        visible={activeModal === 'share'}
        title={t('event.shareEvent')}
        message={translatedActivity.title || activity.title}
        icon="share-2"
        iconColor={Colors.turquoise}
        buttons={[
          {
            label: t('event.shareOutsideTonyte'),
            variant: 'secondary',
            onPress: () => { closeModal(); void shareActivityExternally(activity, t, locale); },
          },
          {
            label: t('event.sendInTonyteChat'),
            variant: 'primary',
            onPress: () => { closeModal(); setShareSheetVisible(true); },
          },
          { label: t('common.cancel'), variant: 'secondary', onPress: closeModal },
        ]}
        onDismiss={closeModal}
      />

      {/* Sign in required */}
      <TonyteModal
        visible={activeModal === 'signIn'}
        title={t('auth.signInRequired')}
        message={signInMsg}
        icon="user"
        iconColor={Colors.sunset}
        buttons={[{ label: t('common.ok'), variant: 'primary', onPress: closeModal }]}
        onDismiss={closeModal}
      />

      {/* Interested or join chat */}
      <TonyteModal
        visible={activeModal === 'interestedOrChat'}
        title={t('event.joinEventChatQuestion')}
        message={getLumaRegistrationUrl(activity) ? `${t('event.joinEventChatPrompt')}\n\n${t('event.registerOnLumaFirst')}` : t('event.joinEventChatPrompt')}
        icon="message-circle"
        iconColor={Colors.turquoise}
        buttons={[
          { label: t('common.cancel'), variant: 'secondary', onPress: closeModal },
          { label: t('event.interestedOnly'), variant: 'secondary', onPress: () => { closeModal(); void registerOnLumaThen(activity, toggle, t); } },
          { label: t('event.addToCalendar'), variant: 'teal', onPress: () => { closeModal(); void registerOnLumaThen(activity, handleInterestedAddToCalendar, t); } },
          { label: t('event.joinChat'), variant: 'primary', onPress: () => { closeModal(); void registerOnLumaThen(activity, handleChat, t); } },
        ]}
        onDismiss={closeModal}
      />

      {/* Plan chat — join first */}
      <TonyteModal
        visible={activeModal === 'planChat'}
        title={t('event.requestToJoinFirst')}
        message={t('event.requestToJoinFirstMsg')}
        icon="users"
        iconColor={Colors.turquoise}
        buttons={[{ label: t('common.gotIt'), variant: 'primary', onPress: closeModal }]}
        onDismiss={closeModal}
      />

      {/* Cancel join request */}
      <TonyteModal
        visible={activeModal === 'cancelRequest'}
        title={t('event.cancelRequestQuestion')}
        message={t('event.cancelRequestMsg')}
        icon="x-circle"
        iconColor={Colors.sunset}
        buttons={[
          { label: t('event.keepRequest'), variant: 'secondary', onPress: closeModal },
          {
            label: t('event.cancelRequest'),
            variant: 'danger',
            onPress: () => {
              closeModal();
              if (joinRequest?.id) {
                void cancelRequest(joinRequest.id).catch(() =>
                  showTonyteToast(t('event.couldNotCancel'), 'error', t('common.tryAgain')));
              }
            },
          },
        ]}
        onDismiss={closeModal}
      />

      {/* Admin: cannot leave, offer to delete */}
      <TonyteModal
        visible={activeModal === 'adminLeave'}
        title={t('event.adminCannotLeave')}
        message={t('event.deletePlanInstead')}
        icon="alert-triangle"
        iconColor="#F87171"
        buttons={[
          { label: t('event.noStay'), variant: 'secondary', onPress: closeModal },
          {
            label: deletingPlan ? '...' : t('event.yesDeletePlan'),
            variant: 'danger',
            onPress: deletingPlan ? () => {} : () => void doDeletePlan(),
          },
        ]}
        onDismiss={deletingPlan ? () => {} : closeModal}
      />

      <LinearGradient
        pointerEvents="none"
        colors={theme.isDark
          ? ['rgba(5,8,18,0)', 'rgba(5,8,18,0.9)', 'rgba(3,6,13,0.98)']
          : ['rgba(245,240,232,0)', 'rgba(245,240,232,0.90)', 'rgba(248,241,231,0.98)']}
        style={styles.bottomFade}
      />
      <View style={[styles.actionDock, appearance.actionDock, { paddingBottom: insets.bottom + 12 }]}>
        {isClubEvent ? (
          <View style={styles.dockGrid}>
            <DockButton variant="secondary" label={t('event.openVenue')} onPress={() => void openLocationInMaps(activity, { provider: 'apple', fallbackTitle: t('event.couldNotOpenMaps'), translate: t })} />
            <DockButton
              variant="primary"
              label={primaryLink ? t('event.viewOfficialEvent') : t('event.shareEvent')}
              icon={primaryLink ? 'external-link' : 'share-2'}
              onPress={() => primaryLink
                ? void openExternalUrl(primaryLink, t('event.couldNotOpenEventLink'), t)
                : shareEvent()}
            />
          </View>
        ) : isCommunityPlan ? (
          <View style={styles.dockGridWide}>
            {isOrganizerOfPlan ? (
              <View style={[styles.dockButton, styles.dockSecondary]}>
                <Icon name="star" size={14} color={Colors.sunset} />
                <Text style={[styles.dockButtonText, { color: Colors.sunset }]}>{t('event.organizer')}</Text>
              </View>
            ) : isPlanParticipant ? (
              <DockButton
                variant="teal"
                label={planJoinPending ? '…' : t('event.leavePlan')}
                icon="log-out"
                onPress={() => void handlePlanLeave()}
              />
            ) : joinRequest?.status === 'pending' ? (
              <DockButton
                variant="secondary"
                label={t('event.requestSentShort')}
                icon="clock"
                onPress={() => setActiveModal('cancelRequest')}
              />
            ) : (
              <DockButton
                variant="secondary"
                label={sendingJoinRequest ? '…' : t('event.requestToJoin')}
                icon="user-plus"
                onPress={handlePlanJoinPress}
              />
            )}
            <DockButton
              variant="primary"
              label={planChatMember ? t('event.openChat') : t('event.planChat')}
              icon="message-circle"
              onPress={handlePlanChat}
            />
          </View>
        ) : (
          <View style={styles.dockGridWide}>
            <DockButton
              variant={interested ? 'teal' : 'secondary'}
              label={t('event.interestedBtn')}
              icon="heart"
              onPress={handleInterestedPress}
            />
            <DockButton variant="primary" label={chatPending ? t('event.joiningChat') : t('event.eventChat')} icon="message-circle" onPress={handleChat} />
          </View>
        )}
      </View>
      {userId && (
        <EventShareSheet
          activity={activity}
          userId={userId}
          userName={auth.currentUser?.displayName ?? 'Traveler'}
          userPhotoURL={auth.currentUser?.photoURL ?? undefined}
          visible={shareSheetVisible}
          onClose={() => setShareSheetVisible(false)}
        />
      )}
      <Modal visible={imageViewerVisible} transparent animationType="fade" onRequestClose={() => setImageViewerVisible(false)}>
        <Pressable style={styles.imageViewer} onPress={() => setImageViewerVisible(false)}>
          {imageSource ? <Image source={typeof imageSource === 'number' ? imageSource : { uri: imageSource }} style={styles.imageViewerImage} resizeMode="contain" /> : null}
          <View style={[styles.imageViewerClose, { top: insets.top + 14 }]}><Icon name="x" size={25} color="#fff" /></View>
        </Pressable>
      </Modal>
    </TonyteBackground>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const RequestReviewCard: React.FC<{
  request: import('../types').PlanJoinRequest;
  onAccept: () => void;
  onDecline: () => void;
}> = ({ request, onAccept, onDecline }) => {
  const { t } = useI18n();
  const profile = useUserProfile(request.fromUserId);
  const displayName = profile ? (profile.displayName || profile.name || 'Traveler') : 'Traveler';
  const photoURL = profile?.profileImage || profile?.profilePhotoURL || '';
  return (
    <View style={styles.reqCard}>
      <View style={styles.reqTop}>
        <View style={styles.reqAvatarWrap}>
          {photoURL ? (
            <Image source={{ uri: photoURL }} style={styles.reqAvatar} resizeMode="cover" resizeMethod="resize" />
          ) : (
            <View style={[styles.reqAvatar, styles.reqAvatarFallback]}>
              <Icon name="user" size={14} color="rgba(255,255,255,0.5)" />
            </View>
          )}
          <VibeVerifiedBadge verified={profile?.faceVerified === true} size={36} />
        </View>
        <View style={styles.reqInfo}>
          <Text style={styles.reqName} numberOfLines={1}>{displayName}</Text>
          {request.message ? (
            <Text style={styles.reqMsg} numberOfLines={2}>"{request.message}"</Text>
          ) : null}
        </View>
      </View>
      <View style={styles.reqBtns}>
        <TouchableOpacity style={styles.reqDecline} onPress={onDecline} activeOpacity={0.8}>
          <Text style={styles.reqDeclineText}>{t('event.decline')}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.reqAccept} onPress={onAccept} activeOpacity={0.8}>
          <Text style={styles.reqAcceptText}>{t('event.accept')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const MetaPill: React.FC<{ icon: string; accent: string; text: string }> = ({ icon, accent, text }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createEventDetailAppearanceStyles(theme), [theme]);
  return <View style={[styles.metaRow, appearance.controlSurface]}>
    <Icon name={icon} size={14} color={accent} />
    <Text style={[styles.metaText, appearance.textSecondary]} numberOfLines={1}>{text}</Text>
  </View>;
};

const GlassCard: React.FC<{ children: React.ReactNode; style?: object }> = ({ children, style }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createEventDetailAppearanceStyles(theme), [theme]);
  return <View style={[styles.glassCard, appearance.glassCard, style]}>
    <LinearGradient
      colors={theme.isDark ? ['rgba(255,255,255,0.095)', 'rgba(255,255,255,0.048)'] : theme.gradients.surfaceElevated}
      style={StyleSheet.absoluteFill}
    />
    <LinearGradient
      colors={theme.isDark
        ? ['rgba(38,208,206,0.09)', 'rgba(242,125,38,0.08)', 'rgba(7,10,19,0.72)']
        : ['rgba(21,155,154,0.045)', 'rgba(242,125,38,0.055)', 'rgba(255,252,246,0.40)']}
      start={{ x: 0.18, y: 0 }}
      end={{ x: 0.9, y: 1 }}
      style={StyleSheet.absoluteFill}
    />
    <View style={styles.glassInner}>{children}</View>
  </View>;
};

const MiniCard: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createEventDetailAppearanceStyles(theme), [theme]);
  return <View style={[styles.miniCard, appearance.controlSurface]}>
    <Text style={[styles.miniLabel, appearance.textMuted]}>{label}</Text>
    <Text style={[styles.miniValue, appearance.textSecondary]}>{value}</Text>
  </View>;
};

const Tag: React.FC<{ label: string }> = ({ label }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createEventDetailAppearanceStyles(theme), [theme]);
  return <View style={[styles.tag, appearance.controlSurface]}>
    <Text style={[styles.tagText, appearance.textSecondary]}>{label}</Text>
  </View>;
};

const SecondaryButton: React.FC<{ icon: string; label: string; onPress: () => void }> = ({ icon, label, onPress }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createEventDetailAppearanceStyles(theme), [theme]);
  return <TouchableOpacity style={[styles.secondaryButton, appearance.controlSurface]} onPress={onPress} activeOpacity={0.84}>
    <Icon name={icon} size={15} color={theme.isDark ? 'rgba(255,255,255,0.82)' : theme.colors.icon} />
    <Text style={[styles.secondaryButtonText, appearance.textPrimary]}>{label}</Text>
  </TouchableOpacity>;
};

const DockButton: React.FC<{
  label: string;
  icon?: string;
  variant: 'primary' | 'secondary' | 'teal';
  onPress: () => void;
}> = ({ label, icon, variant, onPress }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createEventDetailAppearanceStyles(theme), [theme]);
  return <TouchableOpacity
    style={[
      styles.dockButton,
      variant === 'primary' && styles.dockPrimary,
      variant === 'secondary' && styles.dockSecondary,
      variant === 'teal' && styles.dockTeal,
      variant === 'secondary' && appearance.dockSecondary,
    ]}
    onPress={onPress}
    activeOpacity={0.86}
  >
    {icon ? <Icon name={icon} size={15} color={variant === 'teal' ? Colors.navy : (variant === 'secondary' && !theme.isDark ? theme.colors.icon : '#fff')} /> : null}
    <Text style={[styles.dockButtonText, variant === 'teal' && styles.dockButtonTextDark, variant === 'secondary' && appearance.textPrimary]} numberOfLines={1}>{label}</Text>
  </TouchableOpacity>
};

// ── Styles ──────────────────────────────────────────────────────────────────

const createEventDetailAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  content: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  inviteCard: daylightStyle(theme, {
    backgroundColor: 'rgba(255,252,246,0.96)',
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.elevated,
  }),
  glassCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  chipText: daylightStyle(theme, { color: theme.colors.textSecondary }),
  controlSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.border,
  }),
  mapPreview: daylightStyle(theme, {
    backgroundColor: theme.colors.backgroundSecondary,
    borderColor: theme.colors.borderStrong,
  }),
  dockSecondary: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  actionDock: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceGlass,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.navigation,
  }),
});

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#050812' },
  scroll: { flex: 1, minHeight: 0 },
  webDetailScroller: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    overscrollBehaviorY: 'contain',
  } as any,
  // zIndex keeps inviteCard's `bottom: -150` overhang (see inviteEventPosition)
  // painted above the `content` section that follows it in the DOM. RN's own
  // compositing already renders it on top regardless of sibling order, but
  // react-native-web follows strict CSS stacking context rules, so without
  // this the overhanging part of the card got painted over by content's
  // near-opaque background.
  hero: { position: 'relative', zIndex: 1, backgroundColor: '#050812' },
  studiZoneHero: { backgroundColor: '#073B43' },
  studiZoneHeroMark: { position: 'absolute', right: -22, top: 92, transform: [{ rotate: '-10deg' }] },
  studiZoneDotField: {
    position: 'absolute', left: 22, top: 96, width: 116,
    flexDirection: 'row', flexWrap: 'wrap', gap: 22,
    opacity: 0.65,
  },
  studiZoneDot: { width: 4, height: 4, borderRadius: 2, backgroundColor: 'rgba(186,230,253,0.34)' },
  heroImage: { ...StyleSheet.absoluteFill, width: '100%', height: '100%' },
  heroImageBackdrop: { ...StyleSheet.absoluteFillObject, transform: [{ scale: 1.1 }] },
  heroImageShade: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(3,7,15,0.38)' },
  heroImageContained: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  imageViewer: { flex: 1, backgroundColor: 'rgba(0,0,0,0.96)', alignItems: 'center', justifyContent: 'center' },
  imageViewerImage: { width: '100%', height: '100%' },
  imageViewerClose: { position: 'absolute', right: 18, width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(20,24,34,0.78)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
  heroTealBloom: { left: -110, top: 120 },
  heroSunsetBloom: { right: -130, bottom: -60 },
  navButton: {
    width: 44, height: 44, borderRadius: 22,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(3,7,15,0.38)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)',
    ...Shadow.card,
  },
  backButton: { position: 'absolute', left: 16, zIndex: 4 },
  heroActions: { position: 'absolute', right: 16, zIndex: 4, flexDirection: 'row', gap: 8 },
  inviteCard: {
    position: 'absolute', left: 20, right: 20, zIndex: 3,
    borderRadius: 24, padding: 16, overflow: 'hidden',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: 'rgba(3,6,13,0.88)', ...Shadow.strong,
  },
  inviteEventPosition: { bottom: -150 },
  studiZoneInviteCard: {
    borderColor: 'rgba(125,211,252,0.30)',
    backgroundColor: 'rgba(5,25,35,0.94)',
    shadowColor: '#22D3EE',
    shadowOpacity: 0.16,
  },
  planInviteCard: {
    borderRadius: 24, padding: 16, overflow: 'hidden',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: 'rgba(3,6,13,0.88)', ...Shadow.strong,
  },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
  detailManageActions: {
    alignSelf: 'center', flexDirection: 'row',
    justifyContent: 'center', gap: 6, marginTop: 12,
  },
  detailManageButton: {
    paddingHorizontal: 9, paddingVertical: 6, borderRadius: 10,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(10,14,22,0.55)',
  },
  detailManageButtonText: { color: '#fff', fontSize: 10, lineHeight: 12, fontWeight: Typography.extrabold },
  detailManageDeleteText: { color: Colors.sunset },
  detailChip: {
    minHeight: 24, borderRadius: Radius.pill, paddingHorizontal: 10,
    paddingVertical: 5, borderWidth: 1, justifyContent: 'center',
    flexDirection: 'row', alignItems: 'center', gap: 5,
  },
  chipSunset: { backgroundColor: 'rgba(242,125,38,0.16)', borderColor: 'rgba(242,125,38,0.24)' },
  chipTeal: { backgroundColor: 'rgba(38,208,206,0.14)', borderColor: 'rgba(38,208,206,0.22)' },
  chipStudent: { backgroundColor: 'rgba(16,185,129,0.16)', borderColor: 'rgba(167,243,208,0.28)' },
  studentChipText: { color: '#D1FAE5' },
  studentCampusText: { color: '#E0F2FE' },
  chipSoft: { backgroundColor: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.1)' },
  detailChipText: {
    color: 'rgba(255,255,255,0.82)', fontSize: 10, lineHeight: 12,
    fontWeight: Typography.extrabold, textTransform: 'uppercase', letterSpacing: 0.9,
  },
  heroTitle: { color: '#fff', fontSize: 22, lineHeight: 24, fontWeight: Typography.extrabold, marginBottom: 8 },
  heroSubtitle: { color: 'rgba(255,255,255,0.66)', fontSize: 12, lineHeight: 16 },
  studiZoneHeroTitle: { color: '#F0FDFA' },
  studiZoneHeroSubtitle: { color: 'rgba(224,242,254,0.78)' },
  heroMetaGrid: { marginTop: 14, gap: 8 },
  metaRow: {
    minHeight: 33, borderRadius: 16, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.055)',
    flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 11,
  },
  metaText: { flex: 1, color: 'rgba(255,255,255,0.74)', fontSize: 11, fontWeight: Typography.semibold },
  peopleRow: {
    minHeight: 38, borderRadius: 19, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.045)',
    marginTop: 8, paddingHorizontal: 12, flexDirection: 'row',
    alignItems: 'center', justifyContent: 'space-between',
  },
  studiZonePeopleRow: { backgroundColor: 'rgba(125,211,252,0.08)', borderColor: 'rgba(125,211,252,0.18)' },
  peopleLeft: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 8 },
  peopleText: { color: 'rgba(255,255,255,0.76)', fontSize: 12, fontWeight: Typography.semibold },
  content: { paddingHorizontal: 20, gap: 14, backgroundColor: 'rgba(5,8,18,0.98)' },
  contentEvent: { paddingTop: 190 },
  contentPlan: { paddingTop: 16 },
  glassCard: {
    borderRadius: 24, overflow: 'hidden',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(4,8,15,0.72)', ...Shadow.card,
  },
  glassInner: { padding: 16, gap: 14 },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  cardTitle: { color: '#fff', fontSize: 18, lineHeight: 22, fontWeight: Typography.bold },
  description: { color: 'rgba(255,255,255,0.68)', fontSize: 15, lineHeight: 23 },
  shortNote: { color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 20 },
  miniGrid: { gap: 10 },
  miniCard: {
    borderRadius: 18, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.055)', padding: 14,
  },
  miniLabel: {
    color: 'rgba(255,255,255,0.35)', fontSize: 10,
    textTransform: 'uppercase', letterSpacing: 1.6, fontWeight: Typography.extrabold,
  },
  miniValue: { color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 20, marginTop: 8 },
  tagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  tag: {
    borderRadius: Radius.pill, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.06)',
    paddingHorizontal: 11, paddingVertical: 6,
  },
  tagText: { color: 'rgba(255,255,255,0.68)', fontSize: 12, fontWeight: Typography.bold },
  separator: { height: 1, backgroundColor: 'rgba(255,255,255,0.12)' },
  linkRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 18 },
  textLink: { color: Colors.turquoise, fontSize: 12, fontWeight: Typography.extrabold },
  textLinkMuted: { color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: Typography.extrabold },
  capacityText: { color: 'rgba(255,255,255,0.4)', fontSize: 12 },
  publisherButton: { marginTop: 10, marginBottom: 4 },
  organizerProfileButton: {
    minHeight: 58,
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  organizerProfileAvatar: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(94,64,170,0.72)',
    overflow: 'hidden',
  },
  organizerProfileAvatarImage: { width: '100%', height: '100%' },
  organizerProfileCopy: { flex: 1, minWidth: 0 },
  organizerProfileEyebrow: { color: 'rgba(255,255,255,0.46)', fontSize: 11, fontWeight: Typography.bold },
  organizerProfileName: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold, marginTop: 2 },
  venuePostActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  venuePostActionButton: {
    minWidth: 72,
    height: 42,
    borderRadius: 21,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  venuePostActionText: { color: '#fff', fontSize: 13, fontWeight: Typography.bold },
  venueCommentInputWrap: { flexDirection: 'row', alignItems: 'flex-end', gap: 10 },
  venueCommentInput: {
    flex: 1,
    minHeight: 42,
    maxHeight: 96,
    color: '#fff',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  venueCommentSend: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8B5CF6',
  },
  venueCommentsTitle: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  venueCommentRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  venueCommentAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.10)',
    overflow: 'hidden',
  },
  venueCommentAvatarImage: { width: '100%', height: '100%' },
  venueCommentBody: { flex: 1, minWidth: 0 },
  venueCommentName: { color: '#fff', fontSize: 12, fontWeight: Typography.bold },
  venueCommentText: { color: 'rgba(255,255,255,0.72)', fontSize: 13, lineHeight: 18, marginTop: 2 },
  venueCommentDelete: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  secondaryButton: {
    minHeight: 46, borderRadius: 16, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.08)',
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  secondaryButtonText: { color: 'rgba(255,255,255,0.86)', fontSize: 14, fontWeight: Typography.bold },
  mapPreview: {
    height: 190, borderRadius: 22, overflow: 'hidden',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: '#0b0f1b',
  },
  mapGrid: { ...StyleSheet.absoluteFill, opacity: 0.2, backgroundColor: 'rgba(255,255,255,0.03)' },
  mapRoute: { position: 'absolute', left: 30, right: 30, height: 2, borderRadius: 2, backgroundColor: 'rgba(38,208,206,0.38)' },
  mapRouteOne: { top: 74, transform: [{ rotate: '-14deg' }] },
  mapRouteTwo: { top: 116, transform: [{ rotate: '13deg' }] },
  mapPin: {
    position: 'absolute', left: '50%', top: '42%',
    marginLeft: -23, marginTop: -23, width: 46, height: 46, borderRadius: 23,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(94,64,170,0.94)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.22)', ...Shadow.card,
  },
  mapCopy: { position: 'absolute', left: 16, right: 16, bottom: 16 },
  mapVenue: { color: '#fff', fontSize: 14, fontWeight: Typography.bold },
  mapAddress: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 },
  bottomFade: { position: 'absolute', left: 0, right: 0, bottom: 0, height: 130 },
  actionDock: {
    position: 'absolute', left: 0, right: 0, bottom: 0,
    paddingHorizontal: 14, paddingTop: 16,
    backgroundColor: 'rgba(3,6,13,0.92)',
    borderTopWidth: 1, borderColor: 'rgba(255,255,255,0.12)', ...Shadow.strong,
  },
  dockGrid: { flexDirection: 'row', gap: 10 },
  dockGridWide: { flexDirection: 'row', gap: 10 },
  dockButton: {
    flex: 1, minHeight: 52, borderRadius: 18,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 7, paddingHorizontal: 12,
  },
  dockPrimary: { backgroundColor: '#f25b22' },
  dockSecondary: { backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)' },
  dockTeal: { backgroundColor: Colors.turquoise },
  dockButtonText: { color: '#fff', fontSize: 14, fontWeight: Typography.extrabold },
  dockButtonTextDark: { color: Colors.navy },

  // Plan-specific
  creatorRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  creatorInfo: { flex: 1, gap: 2 },
  creatorByLine: { color: 'rgba(255,255,255,0.40)', fontSize: 12 },
  creatorName: { color: '#fff', fontSize: 15, fontWeight: '700' },
  alwaysFree: { color: '#26D0CE', fontSize: 13, fontWeight: '700' },

  planRiskBar: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)', borderRadius: 12,
    paddingVertical: 12, paddingHorizontal: 16, gap: 12, overflow: 'hidden',
  },
  planRiskStripe: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, borderRadius: 2 },
  planRiskText: { marginLeft: 10, gap: 2 },
  planRiskLabel: { fontSize: 13, fontWeight: '600' },
  planRiskSub: { color: 'rgba(255,255,255,0.45)', fontSize: 11 },

  // Safety modal
  safetyOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.72)',
    justifyContent: 'center', alignItems: 'center', paddingHorizontal: 16,
  },
  safetyCard: {
    width: '100%', backgroundColor: '#0f1520',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: 24, padding: 24, gap: 12,
  },
  safetyTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  safetyPlanName: { color: 'rgba(255,255,255,0.65)', fontSize: 15, lineHeight: 22 },
  safetyWarning: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderRadius: 12, padding: 12,
  },
  safetyWarnText: { flex: 1, fontSize: 13, lineHeight: 19 },
  ackRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  ackBox: {
    width: 22, height: 22, borderRadius: 6,
    borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.30)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center', justifyContent: 'center',
  },
  ackBoxChecked: { backgroundColor: '#F87171', borderColor: '#F87171' },
  ackText: { flex: 1, color: 'rgba(255,255,255,0.70)', fontSize: 13, lineHeight: 18 },
  safetyBtns: { flexDirection: 'row', gap: 8, marginTop: 4 },
  safetyCancel: {
    flex: 1, backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: 14, paddingVertical: 14, alignItems: 'center',
  },
  safetyCancelText: { color: 'rgba(255,255,255,0.60)', fontSize: 15, fontWeight: '600' },
  safetyConfirm: {
    flex: 1, backgroundColor: Colors.sunset, borderRadius: 14,
    paddingVertical: 14, alignItems: 'center',
    shadowColor: Colors.sunset, shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.40, shadowRadius: 10, elevation: 6,
  },
  safetyConfirmText: { color: '#fff', fontSize: 15, fontWeight: '700' },

  // Location locked (non-participants)
  locationLockedWrap: {
    alignItems: 'center', gap: 8, paddingVertical: 20,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)',
  },
  locationLockedText: {
    color: 'rgba(255,255,255,0.50)', fontSize: 13, textAlign: 'center', lineHeight: 19,
    paddingHorizontal: 16,
  },
  locationCityHint: {
    color: 'rgba(255,255,255,0.28)', fontSize: 12, textAlign: 'center',
  },

  // Join request message input
  joinRequestMsgInput: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: 14, color: '#ffffff', fontSize: 14,
    paddingHorizontal: 14, paddingVertical: 10, minHeight: 56, textAlignVertical: 'top',
  },

  // Request badge count
  requestBadge: {
    color: '#26D0CE', fontSize: 16, fontWeight: '700',
  },

  // Request review card
  reqCard: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)',
    borderRadius: 16, padding: 14, gap: 12,
  },
  reqTop: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  reqAvatarWrap: { width: 36, height: 36 },
  reqAvatar: { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.08)' },
  reqAvatarFallback: { alignItems: 'center', justifyContent: 'center' },
  reqInfo: { flex: 1, gap: 4 },
  reqName: { color: '#fff', fontSize: 14, fontWeight: '600' },
  reqMsg: { color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 17, fontStyle: 'italic' },
  reqBtns: { flexDirection: 'row', gap: 8 },
  reqDecline: {
    flex: 1, paddingVertical: 9, borderRadius: 12, alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
  },
  reqDeclineText: { color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: '600' },
  reqAccept: {
    flex: 1, paddingVertical: 9, borderRadius: 12, alignItems: 'center',
    backgroundColor: '#26D0CE',
  },
  reqAcceptText: { color: '#0a0e17', fontSize: 13, fontWeight: '700' },
});

export default EventDetailScreen;
